import { createEvent, keys, objectMap } from '@direct-frontend/stdlib';
import { BaseQueryFn, EndpointBuilder, EndpointDefinitions, TagDescription } from './rtk-query-types';
import type { GetStoreFn } from './types';
import { AnyApi } from './buildCreateApi';
import { AnyEndpointDefinition, AnyMutationDefinition } from './apiSSRModule';
import { EnhanceEndpointsOptions } from './buildCreateApi';

type Tags = ReadonlyArray<TagDescription<string>>;
type EventPayload = {
    tags: Tags;
    reducerPath: string;
};

/**
 * Событие, которое позволяет инвалидировать кеш для глобальных тегов.
 * @internal
 */
const invalidateGlobalTagsEvent = createEvent<EventPayload>();

/** Глобальные теги, на которые уже подписан каждый инстанс RTK Query API. */
const globalInvalidationTagsByApi = new WeakMap<AnyApi, Set<string>>();

/** Значение для отсутствия пути для редьюсера. */
const noReducerPath = 'noReducerPath';

/**
 * Проверяет, является ли тег глобальным тегом.
 *
 * @param tag Название тега.
 * @returns boolean.
 * @internal
 */
function isGlobalInvalidationTag<TagTypes extends string>(tag: TagTypes) {
    return tag.startsWith('global:');
}

/**
 * Возвращает список глобальных тегов.
 *
 * @param tagTypes Список тегов.
 * @returns Список глобальных тегов.
 * @internal
 */
function getGlobalInvalidationTags<TagTypes extends string>(tagTypes: readonly TagTypes[]) {
    return tagTypes.filter(isGlobalInvalidationTag);
}

/**
 * Проверяет, есть ли в списке тегов глобальне теги.
 *
 * @param tagTypes Список тегов.
 * @returns boolean.
 * @internal
 */
function hasGlobalInvalidationTags<TagTypes extends string>(tagTypes: readonly TagTypes[]) {
    return tagTypes.some(isGlobalInvalidationTag);
}

/**
 * Создает новую функцию `onQueryStarted` с возможностью вызова инвалидации глобальных тегов.
 *
 * @param originOnQueryStarted Исходная функция `onQueryStarted`.
 * @param invalidatesTags Параметр, определяющий список тегов для инвалидации.
 * @param reducerPath Путь для редьюсера (`${moduleName}/${apiName}`).
 * @returns Новая функция `onQueryStarted`.
 * @internal
 */
function onQueryStartedWithPublishToGlobalInvalidationTags(
    originOnQueryStarted: AnyMutationDefinition['onQueryStarted'],
    invalidatesTags: AnyMutationDefinition['invalidatesTags'],
    reducerPath: string
): AnyMutationDefinition['onQueryStarted'] {
    return async (arg, api) => {
        await originOnQueryStarted?.(arg, api);

        let tagsToInvalidate;

        try {
            const { data: result } = await api.queryFulfilled;

            // DIRECT-205340: Передавать правильную baseQueryMeta последним аргументом
            tagsToInvalidate =
                typeof invalidatesTags === 'function' ? invalidatesTags(result, null, arg, undefined) : invalidatesTags;
        } catch (error) {
            // DIRECT-205340: Передавать правильную baseQueryMeta последним аргументом
            tagsToInvalidate =
                typeof invalidatesTags === 'function' ? invalidatesTags(null, error, arg, undefined) : invalidatesTags;
        }

        if (tagsToInvalidate) {
            invalidateGlobalTagsEvent.publish({ tags: tagsToInvalidate, reducerPath });
        }
    };
}

/**
 * Подписывается на событие инвалидации глобальных тегов.
 *
 * @param api Инстанс RTK Query.
 * @param definitions Дефинишены эндпоинтов.
 * @param getStore Метод получаения стора.
 * @param tagTypes Список тегов.
 * @param reducerPath Путь для редьюсера (`${moduleName}/${apiName}`).
 */
export function subscribeToGlobalInvalidationTags<Definitions extends EndpointDefinitions, TagTypes extends string>(
    api: AnyApi,
    definitions: Definitions,
    getStore: GetStoreFn,
    tagTypes: readonly TagTypes[] | undefined,
    reducerPath: string
) {
    if (tagTypes && hasGlobalInvalidationTags(tagTypes)) {
        const hasQueryWithProvidesTags = keys(definitions).some(definition =>
            Boolean(definitions[definition]?.providesTags)
        );

        if (hasQueryWithProvidesTags) {
            const newGlobalTags = getGlobalInvalidationTags(tagTypes);
            const subscribedGlobalTags = globalInvalidationTagsByApi.get(api);

            if (subscribedGlobalTags) {
                newGlobalTags.forEach(tag => subscribedGlobalTags.add(tag));
                return;
            }

            const globalTags = new Set<string>(newGlobalTags);
            globalInvalidationTagsByApi.set(api, globalTags);

            invalidateGlobalTagsEvent.subscribe(({ tags, reducerPath: path }: EventPayload) => {
                if (path !== noReducerPath && path === reducerPath) {
                    return;
                }

                const tagsToInvalidate = tags.filter(tag => {
                    if (typeof tag === 'string') {
                        return globalTags.has(tag);
                    }

                    return globalTags.has(tag.type);
                });

                if (tagsToInvalidate.length) {
                    getStore().dispatch(api.util.invalidateTags(tagsToInvalidate));
                }
            });
        }
    }
}

/**
 * Добавляет вызов события инвалидации глобальных тегов.
 *
 * @param definitions Дефинишены эндпоинтов.
 * @param tagTypes Список тегов.
 * @param reducerPath Путь для редьюсера (`${moduleName}/${apiName}`).
 * @returns Функция `builder` для эндпоинтов.
 */
export function addPublishGlobalInvalidationTagsToEndpoints<
    Definitions extends EndpointDefinitions,
    TagTypes extends string,
>(definitions: Definitions, tagTypes: readonly TagTypes[] | undefined, reducerPath: string) {
    const hasGlobalTags = tagTypes && hasGlobalInvalidationTags(tagTypes);

    return <BaseQuery extends BaseQueryFn, Definitions extends EndpointDefinitions, Name extends string = 'api'>(
        builder: EndpointBuilder<BaseQuery, TagTypes, Name>
    ) =>
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        objectMap(definitions, ({ type, ...definition }) => {
            if (type === 'query') {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions,@typescript-eslint/no-explicit-any -- DIRECT-239681
                return builder.query(definition as any);
            }

            if (hasGlobalTags && Boolean(definition.invalidatesTags)) {
                const originOnQueryStarted = definition.onQueryStarted;

                // Используем отдельную переменную, чтобы оставить себе хотя бы какую-то типизацию
                // Эта переменная по типу должна описывать параметры мутации
                const mutationDefinition: AnyMutationDefinition = {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                    ...(definition as unknown as AnyMutationDefinition),
                    onQueryStarted: onQueryStartedWithPublishToGlobalInvalidationTags(
                        originOnQueryStarted,
                        definition.invalidatesTags,
                        reducerPath
                    ),
                };

                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions,@typescript-eslint/no-explicit-any -- DIRECT-239681
                return builder.mutation(mutationDefinition as any);
            }

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions,@typescript-eslint/no-explicit-any -- DIRECT-239681
            return builder.mutation(definition as any);
        }) as Definitions;
}

/**
 * Добавляет подписку и вызов события инвалидации глобальных тегов.
 *
 * @param api Инстанс RTK Query.
 * @param getStore Метод получения стора.
 * @param options Параметры для создания API.
 */
export function setupGlobalInvalidationTags<
    Definitions extends EndpointDefinitions,
    NewDefinitions extends EndpointDefinitions,
    TagTypes extends string,
    NewTagTypes extends string,
>(
    api: AnyApi,
    getStore: GetStoreFn,
    options: EnhanceEndpointsOptions<Definitions, NewDefinitions, TagTypes, NewTagTypes>
) {
    if (options.endpoints && options.addTagTypes && hasGlobalInvalidationTags(options.addTagTypes)) {
        subscribeToGlobalInvalidationTags(api, options.endpoints, getStore, options.addTagTypes, api.reducerPath);

        keys(options.endpoints).forEach(endpointName => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- DIRECT-239681
            // @ts-expect-error
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            const endpoint = options.endpoints[endpointName] as AnyEndpointDefinition;

            if (endpoint.invalidatesTags) {
                const originOnQueryStarted = endpoint.onQueryStarted;

                endpoint.onQueryStarted = onQueryStartedWithPublishToGlobalInvalidationTags(
                    originOnQueryStarted,
                    endpoint.invalidatesTags,
                    api.reducerPath
                );
            }
        });
    }
}

/**
 * Инвалидирует глобальные теги.
 *
 * @param globalTags Список глобальных тегов.
 * @public
 */
export function invalidateGlobalTags(globalTags: Tags) {
    invalidateGlobalTagsEvent.publish({ tags: globalTags, reducerPath: noReducerPath });
}
